import { useEffect, useRef, useState } from 'react'
import { saveToStorage } from '@vend/utilities'
import { useT } from '@transifex/react'
import { ToastNotifications } from '@lightspeed/design-system-react'
import uuid from '@vend/vend-uuid'
import { customersStore } from '../../factories/database/customers-store'
import { $state, $stateParams } from '../../ng-compat/imports'
import {
  useIsCalculatingSale,
  usePaymentManager,
  useSaleManager,
} from '../../ng-compat/hooks'
import {
  PAY_ACCOUNT_BALANCE,
  PAY,
  VEND_API_LOAD_SALE_CLOSE_AFTER_COMPLETE,
} from '../../constants'
import { RootState } from '../../state/store'
import { useAppDispatch, useAppSelector } from '../../state/hooks'
import {
  selectCurrentSale,
  setPayAccountBalanceTransactionId,
  updatePayAccountBalanceAmount,
  attachCustomerToSale,
  removeCustomer,
  removePayAccountBalance,
  setCustomerBalances,
} from '../../state/slices/currentSale/currentSaleSlice'
import {
  isAccountBalancePaymentPaid,
  isClosed,
  isConfirmedReadyToComplete,
  isQuote,
} from '../../functions/sale'
import { STATES } from '../../../../lib/routes'
import { getFeatureFromCache } from '../../util/feature-cache'
import { FEATURE_ON_ACCOUNT_PAY_VIA_REGISTER } from '../../constants/wr-features'
import {
  setPostPaymentRedirectUrl,
  clearPostPaymentRedirectUrl,
} from '../../util/post-payment-redirect'
import { customerBalancesManager } from '../../managers/customer-balances-manager'

import { useLoadSale } from './useLoadSale'
import { useSaleModals } from './useSaleModals'
import { useTransitionToPaymentScreen } from './sale-payment/sale-payment-button/use-transition-to-payment-screen'
import { resolveAbpInitialAmount } from './resolveAbpInitialAmount'

const selectSaleLocalId = (state: RootState) => state.currentSale.localId

export const useSaleInitialising = () => {
  const t = useT()
  const dispatch = useAppDispatch()
  const localId = useAppSelector(selectSaleLocalId)
  const saleManager = useSaleManager()
  const paymentManager = usePaymentManager()
  const { loadSale } = useLoadSale()
  const transitionToPaymentScreen = useTransitionToPaymentScreen()
  const isCalculating = useIsCalculatingSale()
  const [isInitialising, setIsInitialising] = useState(true)
  const hasRun = useRef(false)
  const hasCheckedLSPayRecovery = useRef(false)
  const { openLSPayRecoveryModal } = useSaleModals()
  const shouldCheckSaleState = useRef(false)
  const hasHandledAccountBalanceAction = useRef(false)
  const currentSale = useAppSelector(selectCurrentSale)

  useEffect(() => {
    if (!isInitialising || hasRun.current) {
      return
    }

    const handleInit = async () => {
      hasRun.current = true

      // Sale-less ABP flow: skip sale creation entirely. The terminal
      // does not require a sale object — a synthetic abpTransactionId is used
      // as the LSPay correlation key instead of sale.localId.
      // Guard on !abpTransactionId: on remount after completing payment,
      // $stateParams.action is still 'pay_account_balance'. Without the
      // guard, Sale.tsx remounting after the merchant returns from the
      // payment screen would call reserveInit() again instead of falling
      // through to the stale-ABP cleanup path.
      if (
        $stateParams.action === PAY_ACCOUNT_BALANCE &&
        !currentSale.payAccountBalance?.abpTransactionId &&
        getFeatureFromCache(FEATURE_ON_ACCOUNT_PAY_VIA_REGISTER)
      ) {
        // Pre-empt any concurrent saleManager.init() calls (e.g., customer badge
        // $onInit) by claiming the init slot before setIsInitialising(false) causes
        // Sale.tsx to re-render and mount AngularJS child components.
        // Without this, those callers would run loadOrCreateSale() → newSale() →
        // dispatch(newCurrentSale(...)), wiping payAccountBalance from Redux.
        saleManager.reserveInit()
        shouldCheckSaleState.current = true
        setIsInitialising(false)
        return
      }

      if ($stateParams.quoteId) {
        await loadSale($stateParams.quoteId)
      } else if ($stateParams.sale) {
        await loadSale($stateParams.sale)
      } else {
        // If the merchant navigated away from an ABP flow without completing
        // the payment, payAccountBalance and the reserved init slot are still
        // set. Release the slot and clear ABP Redux state so init() →
        // loadOrCreateSale() doesn't bail on the abpTransactionId guard.
        // However, if the payment was already captured (paymentId is set), keep
        // the ABP state intact — Effect 2 will redirect to the confirmation
        // panel, matching the behaviour of a regular paid-but-not-completed sale.
        const hadStaleAbpState = Boolean(
          currentSale.payAccountBalance?.abpTransactionId
        )
        if (hadStaleAbpState) {
          if (isAccountBalancePaymentPaid(currentSale.payAccountBalance)) {
            // Payment was captured — preserve ABP state for the confirmation
            // panel. reserveInit() is still held; Effect 2 will redirect.
            shouldCheckSaleState.current = true
            setIsInitialising(false)
            return
          }
          // Prevent Effect 3 from firing the ABP block while newCurrentSale()
          // is being dispatched inside saleManager.init() below ($stateParams.action
          // is still 'pay_account_balance' at this point).
          hasHandledAccountBalanceAction.current = true
          saleManager.releaseInit()
          // Clear ABP state before init() so loadOrCreateSale() doesn't
          // hit the abpTransactionId early-return guard and skip sale creation.
          dispatch(removePayAccountBalance())
          clearPostPaymentRedirectUrl()
        }
        await saleManager.init()
        if (hadStaleAbpState) {
          // The stale abpTransactionId is now cleared from Redux. Reset the
          // guard so Effect 3 can handle the fresh ABP on this same navigation
          // when setIsInitialising(false) fires below.
          hasHandledAccountBalanceAction.current = false
        }
      }

      if ($stateParams.closeAfterComplete) {
        saveToStorage(VEND_API_LOAD_SALE_CLOSE_AFTER_COMPLETE, {
          closeAfterComplete: $stateParams.closeAfterComplete,
        })
      }

      shouldCheckSaleState.current = true
      setIsInitialising(false)
    }

    handleInit()
  }, [
    currentSale.payAccountBalance,
    dispatch,
    isInitialising,
    loadSale,
    saleManager,
  ])

  useEffect(() => {
    if (hasCheckedLSPayRecovery.current || isInitialising) {
      return
    }
    hasCheckedLSPayRecovery.current = true
    const {
      registerSalePaymentId,
    } = paymentManager.checkAndRetriveActivePayment(localId)
    if (!registerSalePaymentId) {
      return
    }

    if (!isCalculating) {
      saleManager.queueSaleCalculation()
    }
    openLSPayRecoveryModal(localId, complete => {
      complete.then(transitionToPaymentScreen)
    })
  }, [
    isCalculating,
    isInitialising,
    localId,
    openLSPayRecoveryModal,
    paymentManager,
    saleManager,
    transitionToPaymentScreen,
  ])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!shouldCheckSaleState || isInitialising || !currentSale) {
      return
    }

    shouldCheckSaleState.current = false

    if (
      isClosed(currentSale.status) ||
      isConfirmedReadyToComplete(currentSale) ||
      isQuote(currentSale.status) ||
      isAccountBalancePaymentPaid(currentSale.payAccountBalance)
    ) {
      $state.go(STATES.SALE_CONFIRMATION)
      return
    }

    const action = $stateParams.action
    if (action === PAY) {
      saleManager.queueSaleCalculation().then(transitionToPaymentScreen)
      return
    }

    // Account balance payment flow: navigate here from monocle with
    // /webregister/pay-account-balance?customerId=<id>&onPaymentCompletedRedirectUrl=<path>
    // Sale-less ABP flow: no sale is created. A synthetic abpTransactionId is generated
    // as the LSPay correlation key. The customer is fetched from IndexedDB,
    // customerBalances are read, and we go directly to the payment screen.
    //
    // TWO-SIGNAL DESIGN (intentional):
    // 1. peekPostPaymentRedirectUrl() — synchronous, set immediately from URL params.
    //    Used by UI guards (PaymentPanel, CustomerSection, useAmountTenderedMessages)
    //    to suppress non-ABP behaviour on FIRST RENDER, before any async work completes.
    // 2. payAccountBalance.abpTransactionId — async, set AFTER customer lookup succeeds.
    //    Used for business logic (LSPay correlation, payment submission, receipt).
    //    Cannot be set earlier because we must validate the customer exists first.
    //
    // These are NOT redundant signals — they represent different lifecycle moments.
    // The redirect URL answers "did we enter via ABP?" (navigation intent).
    // The abpTransactionId answers "is the ABP flow ready for payment?" (data readiness).
    if (
      $stateParams.action === PAY_ACCOUNT_BALANCE &&
      !hasHandledAccountBalanceAction.current &&
      getFeatureFromCache(FEATURE_ON_ACCOUNT_PAY_VIA_REGISTER)
    ) {
      hasHandledAccountBalanceAction.current = true

      const customerId = $stateParams.customerId as string | null
      const redirectUrl = $stateParams.onPaymentCompletedRedirectUrl as
        | string
        | null
      // Pre-selected amount from monocle modal (optional)
      const prefilledAmountParam = $stateParams.amount as string | null

      if (redirectUrl) {
        setPostPaymentRedirectUrl(redirectUrl)
      }

      if (!customerId) {
        // No customer ID — degrade gracefully and stay on the sell screen.
        // Release the reserved init slot so subsequent saleManager.init() calls
        // run loadOrCreateSale() normally.
        saleManager.releaseInit()
        clearPostPaymentRedirectUrl()
        return
      }

      customersStore
        .get(customerId)
        .then(customer => {
          if (!customer?.id) {
            saleManager.releaseInit()
            clearPostPaymentRedirectUrl()
            ToastNotifications.negative(
              t(
                'Customer data not yet synced — please search for the customer manually.',
                {
                  _context:
                    'Error when customer is missing from webregister IndexedDB during account balance payment flow',
                }
              )
            )
            return
          }

          // Generate a synthetic UUID as the LSPay correlation key.
          // This replaces sale.localId for the purpose of terminal payment
          // tracking and recovery, since no sale is created in the sale-less ABP flow.
          const abpTransactionId = uuid()
          dispatch(setPayAccountBalanceTransactionId(abpTransactionId))

          // Attach the customer to the Redux sale so the payment panel shows
          // the customer name and enables customer-gated payment types.
          dispatch(attachCustomerToSale(customer))

          // Fetch fresh balances via API — same path as saleManager.addCustomer
          // but without creating a sale.
          customerBalancesManager
            .getCustomerBalances(customer)
            .then(balances => {
              const absBalance =
                balances.account < 0 ? Math.abs(balances.account) : 0

              if (absBalance === 0) {
                // Customer has no outstanding debt — defensive guard in case
                // the balance was settled between monocle loading and the
                // merchant clicking "Pay", or if the URL was navigated to
                // manually.
                // Clean up Redux state set above (abpTransactionId + customer).
                saleManager.releaseInit()
                dispatch(removeCustomer())
                dispatch(removePayAccountBalance())
                clearPostPaymentRedirectUrl()
                ToastNotifications.negative(
                  t('Customer has no outstanding account balance.', {
                    _context:
                      'Error shown when the account balance payment flow is triggered but the customer owes nothing',
                  })
                )
                return
              }

              // Use pre-filled amount from monocle modal if provided and valid (positive
              // finite number), capped to the actual balance. Falls back to full balance
              // if the param is absent, non-numeric, or out of range.
              dispatch(
                updatePayAccountBalanceAmount(
                  resolveAbpInitialAmount(prefilledAmountParam, absBalance)
                )
              )
              // Dispatch customer balances before navigating so that
              // useAmountTenderedMessages sees maxBalance = absBalance (not 0)
              // on first render. Without this, customerBalances = null at mount
              // because attachCustomerToSale resets it, and the capping logic
              // resolves to Math.min(absBalance, 0) = 0, wiping the amount.
              dispatch(setCustomerBalances(balances))
              $state.go(STATES.PAYMENT, { lspayQuickPaySelected: false })
            })
            .catch(() => {
              // Balance fetch failed — clean up ABP state so the sale screen is usable.
              saleManager.releaseInit()
              dispatch(removeCustomer())
              clearPostPaymentRedirectUrl()
              ToastNotifications.negative(
                t('Unable to fetch customer balance. Please try again.', {
                  _context:
                    'Error when balance API fails during account balance payment flow',
                })
              )
            })
        })
        .catch(() => {
          saleManager.releaseInit()
          clearPostPaymentRedirectUrl()
          ToastNotifications.negative(
            t('Unable to load customer data. Please try again.', {
              _context:
                'Error when IndexedDB lookup fails during account balance payment flow',
            })
          )
        })
      return
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentSale,
    dispatch,
    isInitialising,
    saleManager,
    t,
    transitionToPaymentScreen,
  ])

  return isInitialising
}
